import { useChannelAvatar } from "@/lib/hooks/conversations/use-channel-avatar.tsx";
import { useChannelPriority } from "@/lib/hooks/conversations/use-channel-priority.ts";
import { useIsChannelOnline } from "@/lib/hooks/conversations/use-is-channel-online.ts";
import { cn } from "@/lib/utils.ts";
import type { GroupChannel } from "@sendbird/chat/groupChannel";
import { TypingIndicatorText } from "@sendbird/uikit-react/GroupChannel/components/TypingIndicator";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { shouldShowCheckinBadge } from "@/lib/utils/check-in-badge";
import { SignalIcon } from "@heroicons/react/24/outline";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useGetMyMentorships } from "@/lib/api/mentorships";
import {
  buildRedactedPseudoId,
  getPreferredName,
} from "@/lib/redacted-display";
import { isMagicMomentBannerExpired } from "@/lib/utils/magic-moments";
import {
  devOverridesEnabled,
  useDevOverridesStore,
} from "@/lib/hooks/admin/use-dev-overrides";

interface ConversationListItemProps {
  channel: GroupChannel;
  currentChannelUrl: string | null;
  onSelect: (url: string) => void;
  shouldEverShowCheckinBadge?: boolean;
}

export function ConversationListItem({
  channel,
  currentChannelUrl,
  onSelect,
  shouldEverShowCheckinBadge = false,
}: ConversationListItemProps) {
  const store = useSendbirdStateContext();
  const { track } = useTrackEvent();
  const devForceAtRiskMomentum = useDevOverridesStore(
    (s) => s.forceAtRiskMomentum,
  );
  const hidePhiPii = useDevOverridesStore((s) => s.hidePhiPii) === true;

  const { data: priority } = useChannelPriority(channel);
  const { lastMessage, unreadMessageCount } = channel;
  const { data: mentorships } = useGetMyMentorships();
  const mentorship = useMemo(
    () => mentorships?.find((m) => m.sdk_url === channel.url),
    [mentorships, channel.url],
  );

  const channelTitle = useMemo(() => {
    const preferredTeenName =
      !hidePhiPii && mentorship?.mentee
        ? getPreferredName(mentorship.mentee)
        : "";
    if (preferredTeenName) return preferredTeenName;

    const membersNamesExcludingMe = channel.members
      .filter((member) => member.userId !== store.config.userId)
      .map((member) =>
        hidePhiPii
          ? buildRedactedPseudoId(member.userId)
          : member.nickname || member.userId,
      )
      .join(", ");

    return membersNamesExcludingMe.length < 30
      ? membersNamesExcludingMe.length === 0
        ? "(No members)"
        : membersNamesExcludingMe
      : `${membersNamesExcludingMe.slice(0, 30)}...`;
  }, [store.config.userId, channel, mentorship, hidePhiPii]);

  const showCheckinBadge = useMemo(() => {
    return (
      shouldEverShowCheckinBadge &&
      shouldShowCheckinBadge(channel, store.config.userId)
    );
  }, [channel, store.config.userId, shouldEverShowCheckinBadge]);

  const lastMessageText = useMemo(() => {
    if (hidePhiPii || !lastMessage) return null;

    const lastMessageObj: any = lastMessage;

    if (
      lastMessageObj.messageType === "admin" ||
      lastMessageObj.messageType === "user"
    ) {
      return lastMessageObj.message.length < 30
        ? lastMessageObj.message
        : `${lastMessageObj.message.slice(0, 30)}...`;
    }

    if (lastMessageObj.messageType === "file") {
      return lastMessageObj.url.length < 30
        ? lastMessageObj.url
        : `${lastMessageObj.url.slice(0, 30)}...`;
    }

    return null;
  }, [hidePhiPii, lastMessage]);

  const isOnline = useIsChannelOnline(channel, store.config.userId);

  const isNewTeen = useMemo(() => {
    const createdAt = new Date(channel.createdAt).getTime();
    const now = Date.now();
    const diffTime = now - createdAt;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    return diffDays < 7;
  }, [channel.createdAt]);

  const isDay2 = useMemo(() => {
    const magicMoment = mentorship?.metadata?.magic_moment;
    return (
      magicMoment?.day_state === "day2" &&
      !isMagicMomentBannerExpired(
        magicMoment?.day_state,
        magicMoment?.expires_at,
      )
    );
  }, [mentorship]);

  const isAtRiskMomentum =
    (devOverridesEnabled && devForceAtRiskMomentum) ||
    mentorship?.metadata?.at_risk_momentum?.status === "active";

  const channelAvatar = useChannelAvatar(
    channel,
    store.config.userId,
    !!priority,
    isOnline,
    isNewTeen,
  );

  const timestampText = useMemo(() => {
    if (lastMessage) {
      return new Date(lastMessage.createdAt).toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    }
  }, [lastMessage]);

  const otherMember = useMemo(() => {
    return channel.members.find(
      (member) => member.userId !== store.config.userId,
    );
  }, [channel.members, store.config.userId]);

  const handleClick = () => {
    track(PostHogEvents.OPENED_CHAT, {
      channel_url: channel.url,
      teen_id: otherMember?.userId,
      teen_name: otherMember?.nickname,
    });
    onSelect(channel.url);
  };

  return (
    <div
      className={cn(
        "flex w-full cursor-pointer flex-row items-center gap-2 px-4 py-3 hover:bg-gray-50",
        {
          "bg-indigo-100": currentChannelUrl === channel.url,
        },
      )}
      onClick={handleClick}
    >
      <div
        className={cn("h-2 w-2 rounded-full", {
          "bg-blue-700": unreadMessageCount > 0,
        })}
      />

      {channelAvatar}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-row items-center justify-between">
          <div className="mr-2 flex min-w-0 flex-1 items-center">
            <div className="flex min-w-0 flex-1 items-center">
              <span className="truncate text-sm font-medium text-black">
                {channelTitle}
              </span>
              {showCheckinBadge && (
                <Label className="ml-2 whitespace-nowrap rounded-xs bg-[#FFDAD4] px-2 py-1 text-xs">
                  Check-in
                </Label>
              )}
              {isAtRiskMomentum && (
                <Label className="ml-2 whitespace-nowrap rounded-md bg-[#DCFCE7] px-2 py-0.5 text-xs font-medium text-[#166534]">
                  At risk
                </Label>
              )}
              {isDay2 && (
                <Label className="ml-2 whitespace-nowrap rounded-md bg-[#E5E5FF] px-2 py-0.5 text-xs font-medium text-[#4F46E5]">
                  Day 2
                </Label>
              )}
            </div>
          </div>
          <span className="shrink-0 text-xs font-normal text-zinc-400">
            {timestampText}
          </span>
        </div>

        <span className="truncate text-xs font-normal text-stone-500">
          {channel.isTyping ? (
            <TypingIndicatorText members={channel.getTypingUsers()} />
          ) : (
            lastMessageText || "No messages"
          )}
        </span>

        {isNewTeen && (
          <span className="flex flex-row items-center gap-1 text-xs font-normal text-[#138A00]">
            <SignalIcon className="h-3 w-3" />
            New Teen
          </span>
        )}
      </div>
    </div>
  );
}
